// =====================================================
// Swiss Finance OS - Verzeichnis der Verarbeitungstätigkeiten
// DSGVO Art. 30 / FADP Art. 10 konforme Dokumentation
// =====================================================

export interface ProcessingActivity {
  id: string;
  name: string;
  purpose: string;
  legalBasis: string;
  dataCategories: string[];
  dataSubjects: string[];
  recipients: string[];
  thirdCountryTransfers: string[];
  retentionPeriod: string;
  technicalMeasures: string[];
  organizationalMeasures: string[];
  dataProtectionImpactAssessment: boolean;
  lastUpdated: string;
}

export const processingActivities: ProcessingActivity[] = [
  {
    id: "PA-001",
    name: "Kundenverwaltung und Mandantenbetreuung",
    purpose: "Verwaltung von Kundenstammdaten, Mandantenbetreuung, Kommunikation",
    legalBasis: "Art. 6(1)(b) DSGVO (Vertragserfüllung), Art. 6(1)(f) DSGVO (Berechtigtes Interesse)",
    dataCategories: [
      "Name, Vorname, Firmenname",
      "Anschrift (Straße, PLZ, Ort)",
      "Kontaktdaten (E-Mail, Telefon)",
      "Geburtsdatum (bei natürlichen Personen)",
      "Handelsregisternummer (bei Unternehmen)",
      "Steuernummer, UID"
    ],
    dataSubjects: [
      "Kunden (natürliche Personen)",
      "Geschäftsführer von Kundenunternehmen",
      "Ansprechpartner bei Kundenunternehmen"
    ],
    recipients: [
      "Togeco Buchhaltung (interne Mitarbeiter)",
      "Steuerberater und Wirtschaftsprüfer",
      "Behörden (bei gesetzlichen Meldepflichten)",
      "IT-Dienstleister (Auftragsverarbeiter)"
    ],
    thirdCountryTransfers: [
      "Supabase (EU-Server, Standardvertragsklauseln)",
      "OpenAI/Anthropic (USA, mit Anonymisierung und Standardvertragsklauseln)"
    ],
    retentionPeriod: "10 Jahre nach Ende des Mandatsverhältnisses",
    technicalMeasures: [
      "SSL/TLS-Verschlüsselung",
      "Datenbankverschlüsselung",
      "Zugriffskontrollen",
      "Regelmäßige Backups",
      "Firewall und Intrusion Detection"
    ],
    organizationalMeasures: [
      "Datenschutzschulungen für Mitarbeiter",
      "Zugriffsberechtigungen nach dem Need-to-know-Prinzip",
      "Regelmäßige Sicherheitsaudits",
      "Datenschutzrichtlinien und -verfahren"
    ],
    dataProtectionImpactAssessment: false,
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PA-002",
    name: "Buchhaltungsverarbeitung und Transaktionsdaten",
    purpose: "Verarbeitung von Buchhaltungsdaten, Transaktionsklassifizierung, Buchungserstellung",
    legalBasis: "Art. 6(1)(b) DSGVO (Vertragserfüllung), Art. 6(1)(c) DSGVO (Rechtliche Verpflichtung)",
    dataCategories: [
      "Bankverbindungen (IBAN, BIC)",
      "Transaktionsdaten (Datum, Betrag, Gegenpartei)",
      "Rechnungsdaten",
      "Belege und Dokumente",
      "Buchungszeilen und Konten",
      "Steuerrelevante Informationen"
    ],
    dataSubjects: [
      "Kunden (natürliche Personen)",
      "Geschäftspartner der Kunden",
      "Mitarbeiter der Kundenunternehmen"
    ],
    recipients: [
      "Togeco Buchhaltung (interne Mitarbeiter)",
      "KI-Service-Provider (mit Anonymisierung)",
      "Behörden (bei gesetzlichen Meldepflichten)",
      "Steuerberater und Wirtschaftsprüfer"
    ],
    thirdCountryTransfers: [
      "OpenAI/Anthropic (USA, mit Anonymisierung und Standardvertragsklauseln)",
      "Mistral AI (EU, falls verwendet)"
    ],
    retentionPeriod: "10 Jahre nach Ende des Geschäftsjahres (Art. 958f OR)",
    technicalMeasures: [
      "End-to-End-Verschlüsselung",
      "Anonymisierung vor KI-Verarbeitung",
      "Datenbankverschlüsselung",
      "Sichere API-Kommunikation",
      "Regelmäßige Sicherheitsupdates"
    ],
    organizationalMeasures: [
      "Strenge Zugriffskontrollen",
      "Audit-Trail für alle Datenzugriffe",
      "Regelmäßige Compliance-Prüfungen",
      "Schulungen zur Datenanonymisierung"
    ],
    dataProtectionImpactAssessment: true,
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PA-003",
    name: "KI-gestützte Transaktionsklassifizierung",
    purpose: "Automatische Klassifizierung von Transaktionen mittels Künstlicher Intelligenz",
    legalBasis: "Art. 6(1)(b) DSGVO (Vertragserfüllung), Art. 6(1)(f) DSGVO (Berechtigtes Interesse)",
    dataCategories: [
      "Anonymisierte Transaktionsdaten",
      "Klassifizierungsvorschläge",
      "Vertrauenswerte der KI",
      "Benutzerkorrekturen und Feedback"
    ],
    dataSubjects: [
      "Kunden (indirekt über anonymisierte Daten)"
    ],
    recipients: [
      "Togeco Buchhaltung (interne Mitarbeiter)",
      "KI-Service-Provider (OpenAI, Anthropic, Mistral)",
      "Entwickler und Systemadministratoren"
    ],
    thirdCountryTransfers: [
      "OpenAI (USA, mit Anonymisierung und Standardvertragsklauseln)",
      "Anthropic (USA, mit Anonymisierung und Standardvertragsklauseln)",
      "Mistral AI (EU, falls verwendet)"
    ],
    retentionPeriod: "2 Jahre nach Verarbeitung",
    technicalMeasures: [
      "Strikte Datenanonymisierung vor KI-Übertragung",
      "Pseudonymisierung von sensiblen Daten",
      "Verschlüsselte API-Kommunikation",
      "Lokale Zwischenspeicherung",
      "Automatische Löschung nach Verarbeitung"
    ],
    organizationalMeasures: [
      "Datenschutz-Folgenabschätzung durchgeführt",
      "Regelmäßige Überprüfung der Anonymisierung",
      "Schulungen zur KI-Datenschutz-Compliance",
      "Verträge mit KI-Providern mit Datenschutzklauseln"
    ],
    dataProtectionImpactAssessment: true,
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PA-004",
    name: "Website-Betrieb und technische Daten",
    purpose: "Betrieb der Website, technische Funktionalität, Nutzungsanalyse",
    legalBasis: "Art. 6(1)(f) DSGVO (Berechtigtes Interesse), Art. 6(1)(a) DSGVO (Einwilligung für Analytics)",
    dataCategories: [
      "IP-Adressen",
      "Browser-Informationen",
      "Geräteinformationen",
      "Nutzungsdaten der Website",
      "Cookies und ähnliche Technologien",
      "Session-Daten"
    ],
    dataSubjects: [
      "Website-Besucher",
      "Anwendungsnutzer"
    ],
    recipients: [
      "Togeco Buchhaltung (interne Mitarbeiter)",
      "Hosting-Provider",
      "Analytics-Provider (mit Einwilligung)",
      "CDN-Provider"
    ],
    thirdCountryTransfers: [
      "Cloudflare (USA, Standardvertragsklauseln)",
      "Google Analytics (USA, mit Einwilligung und Standardvertragsklauseln)"
    ],
    retentionPeriod: "Maximal 2 Jahre",
    technicalMeasures: [
      "SSL/TLS-Verschlüsselung",
      "IP-Anonymisierung",
      "Cookie-Management",
      "Regelmäßige Sicherheitsupdates",
      "DDoS-Schutz"
    ],
    organizationalMeasures: [
      "Datenschutzfreundliche Voreinstellungen",
      "Transparente Cookie-Informationen",
      "Einfache Opt-out-Möglichkeiten",
      "Regelmäßige Datenschutz-Reviews"
    ],
    dataProtectionImpactAssessment: false,
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PA-005",
    name: "E-Mail-Kommunikation",
    purpose: "Geschäftliche Kommunikation mit Kunden, Behörden und Partnern",
    legalBasis: "Art. 6(1)(b) DSGVO (Vertragserfüllung), Art. 6(1)(f) DSGVO (Berechtigtes Interesse)",
    dataCategories: [
      "E-Mail-Adressen",
      "E-Mail-Inhalte",
      "Anhänge und Dokumente",
      "Metadaten (Zeitstempel, Betreff)",
      "Kommunikationsverlauf"
    ],
    dataSubjects: [
      "Kunden",
      "Behördenvertreter",
      "Geschäftspartner",
      "Mitarbeiter"
    ],
    recipients: [
      "Togeco Buchhaltung (interne Mitarbeiter)",
      "E-Mail-Provider",
      "Empfänger der E-Mails",
      "IT-Dienstleister"
    ],
    thirdCountryTransfers: [
      "Microsoft 365 (USA, Standardvertragsklauseln)",
      "Google Workspace (USA, Standardvertragsklauseln)"
    ],
    retentionPeriod: "10 Jahre (geschäftliche E-Mails), 2 Jahre (technische E-Mails)",
    technicalMeasures: [
      "E-Mail-Verschlüsselung (TLS)",
      "Spam- und Virenschutz",
      "Sichere E-Mail-Server",
      "Regelmäßige Backups",
      "Zugriffskontrollen"
    ],
    organizationalMeasures: [
      "E-Mail-Richtlinien",
      "Schulungen zur sicheren E-Mail-Nutzung",
      "Regelmäßige Passwort-Updates",
      "Zwei-Faktor-Authentifizierung"
    ],
    dataProtectionImpactAssessment: false,
    lastUpdated: new Date().toISOString()
  },
  {
    id: "PA-006",
    name: "Rechtliche Aufbewahrungspflichten",
    purpose: "Erfüllung gesetzlicher Aufbewahrungspflichten für Buchhaltungs- und Steuerdaten",
    legalBasis: "Art. 6(1)(c) DSGVO (Rechtliche Verpflichtung)",
    dataCategories: [
      "Alle Buchhaltungsdaten",
      "Steuerrelevante Dokumente",
      "Geschäftskorrespondenz",
      "Belege und Rechnungen",
      "Jahresabschlüsse",
      "Steuererklärungen"
    ],
    dataSubjects: [
      "Kunden (natürliche Personen)",
      "Geschäftsführer von Kundenunternehmen",
      "Geschäftspartner der Kunden"
    ],
    recipients: [
      "Togeco Buchhaltung (interne Mitarbeiter)",
      "Steuerbehörden",
      "Wirtschaftsprüfer",
      "Gerichte (bei rechtlichen Verfahren)"
    ],
    thirdCountryTransfers: [],
    retentionPeriod: "10 Jahre nach Ende des Geschäftsjahres (Art. 958f OR)",
    technicalMeasures: [
      "Sichere Archivierung",
      "Verschlüsselte Speicherung",
      "Regelmäßige Backups",
      "Integritätssicherung",
      "Zugriffskontrollen"
    ],
    organizationalMeasures: [
      "Klare Archivierungsrichtlinien",
      "Regelmäßige Compliance-Prüfungen",
      "Schulungen zu Aufbewahrungspflichten",
      "Dokumentierte Löschverfahren"
    ],
    dataProtectionImpactAssessment: false,
    lastUpdated: new Date().toISOString()
  }
];

// Hilfsfunktionen für das Verzeichnis
export const getProcessingActivityById = (id: string): ProcessingActivity | undefined => {
  return processingActivities.find(activity => activity.id === id);
};

export const getProcessingActivitiesByDataCategory = (category: string): ProcessingActivity[] => {
  return processingActivities.filter(activity => 
    activity.dataCategories.some(cat => 
      cat.toLowerCase().includes(category.toLowerCase())
    )
  );
};

export const getProcessingActivitiesWithThirdCountryTransfers = (): ProcessingActivity[] => {
  return processingActivities.filter(activity => 
    activity.thirdCountryTransfers.length > 0
  );
};

export const getProcessingActivitiesRequiringDPIA = (): ProcessingActivity[] => {
  return processingActivities.filter(activity => 
    activity.dataProtectionImpactAssessment
  );
};

// Export für Compliance-Reporting
export const generateComplianceReport = () => {
  return {
    totalActivities: processingActivities.length,
    activitiesWithDPIA: getProcessingActivitiesRequiringDPIA().length,
    activitiesWithThirdCountryTransfers: getProcessingActivitiesWithThirdCountryTransfers().length,
    lastUpdated: new Date().toISOString(),
    activities: processingActivities.map(activity => ({
      id: activity.id,
      name: activity.name,
      purpose: activity.purpose,
      legalBasis: activity.legalBasis,
      dataProtectionImpactAssessment: activity.dataProtectionImpactAssessment,
      thirdCountryTransfers: activity.thirdCountryTransfers.length > 0
    }))
  };
};
